'use client';

import { useState, useEffect, useCallback } from 'react';
import { graphql } from 'react-relay';
import { useRouter } from 'next/navigation';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useTranslation } from '@/helpers/translate';
import { useWasUrl } from '@/helpers/router';
import { useZusUserConnectedStore } from '@/helpers/user-auth.js';
import Loading from '@/core/loading';
import miniQLWS from '@/helpers/miniQueryLoaderv3.js';
import { SubscribeBlock } from './legendCard';

/**
 * WEA-157 — Public Legend+ offer block (hall-of-fame page).
 *
 * Prices come from the PUBLIC catalogue query (no auth) so a visitor sees the real Stripe amounts.
 * Clicking a plan routes on the viewer's state:
 *  - not logged in      -> open the login/register modal (SIRET flow gives them a corpo), and the
 *                          intent is replayed once a corpoRef shows up.
 *  - corpo, no sub      -> open the card/SEPA modal right on the chosen plan (any member may
 *                          subscribe: the subscription belongs to the corpo, cf. CorpoAbac).
 *  - corpo already sub  -> no modal, send them to the Legend+ page.
 *  - logged in, no corpo-> send them to the Legend+ page (corpo journey lives there).
 */
const queryPublicPlans = graphql`
  query legendPlusOffersQuery {
    payment {
      corpo {
        plans: getPublicPlans
      }
    }
  }
`;

const LOCALE_BY_LANG = { fr: 'fr-FR', en: 'en-GB', es: 'es-ES' };

function fmtMoney(amount, currency, lang = 'fr') {
  if (amount == null) return '';
  const locale = LOCALE_BY_LANG[lang] || LOCALE_BY_LANG.fr;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: (currency || 'eur').toUpperCase(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (err) {
    return `${amount} ${(currency || 'eur').toUpperCase()}`;
  }
}

/** Savings of the committed plan vs the no-commitment one. Derived, never hardcoded. */
function offerSavings(plan, plans) {
  if (!plan?.engaged) return null;
  const reference = (plans || []).find((p) => !p.engaged);
  const referenceAmount = reference?.amount;
  if (!referenceAmount || !plan.amount || referenceAmount <= plan.amount) return null;
  const months = plan.commitmentMonths || 12;
  return {
    percent: Math.round((1 - (plan.amount / referenceAmount)) * 100),
    months,
    referenceAmount,
    total: Math.round(plan.amount * months * 100) / 100,
    referenceTotal: Math.round(referenceAmount * months * 100) / 100,
  };
}

function OfferCard({
  plan, plans, selected, dimmed, onSelect, onHover, t, lang,
}) {
  const savings = offerSavings(plan, plans);
  const handleClick = useCallback(() => onSelect(plan.planType), [onSelect, plan.planType]);
  const handleEnter = useCallback(() => onHover(plan.planType), [onHover, plan.planType]);
  const handleLeave = useCallback(() => onHover(''), [onHover]);
  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
      className={`flex flex-col gap-2 p-5 text-left rounded-2xl border transition-all duration-200 hover:border-red hover:bg-red/10 hover:opacity-100 ${
        selected ? 'border-red bg-red/15 ring-1 ring-red' : 'border-white/20'
      } ${dimmed ? 'opacity-50' : ''}`}
    >
      <div className="flex gap-2 justify-between items-start">
        <span className="text-sm font-medium text-red">
          {plan.engaged
            ? t('corpo:abonnement.offerEngagedTitle')
            : t('corpo:abonnement.offerFlexTitle')}
        </span>
        {savings?.percent > 0 && (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full text-red bg-red/20 shrink-0">
            {t('corpo:abonnement.offerSavingBadge', { percent: savings.percent })}
          </span>
        )}
      </div>
      <div className="flex gap-2 items-baseline">
        {savings?.percent > 0 && (
          <span className="text-sm line-through text-white/40">
            {fmtMoney(savings.referenceAmount, plan.currency, lang)}
          </span>
        )}
        <span className="text-4xl font-medium text-white">
          {fmtMoney(plan.amount, plan.currency, lang)}
        </span>
        <span className="text-sm text-white/60">{t('corpo:abonnement.offerPerMonth')}</span>
      </div>
      <p className="text-xs leading-relaxed text-white/60">
        {savings?.percent > 0
          ? t('corpo:abonnement.offerEngagedDetail', {
            total: fmtMoney(savings.total, plan.currency, lang),
            months: savings.months,
            reference: fmtMoney(savings.referenceTotal, plan.currency, lang),
          })
          : t('corpo:abonnement.offerFlexDetail')}
      </p>
      <span
        className={`mt-1 px-4 py-2.5 text-sm font-medium text-center rounded-full ${
          selected ? 'text-white bg-red' : 'text-red bg-red/15'
        }`}
      >
        {selected ? t('corpo:abonnement.offerSelected') : t('corpo:abonnement.offerChoose')}
      </span>
    </button>
  );
}

function LegendPlusOffersContent({ data }) {
  const { t, lang } = useTranslation();
  const router = useRouter();
  const { getWasUrl } = useWasUrl({ lang });
  const legendHref = getWasUrl('/corpo/legend');
  const userData = useZusUserConnectedStore((state) => state.userData);
  const userAuthOpenModal = useZusUserConnectedStore((state) => state.userAuthOpenModal);
  const [stripe, setStripe] = useState(null);
  const [modalPlan, setModalPlan] = useState('');
  const [hovered, setHovered] = useState('');
  // Intent kept across the login/register detour: replayed as soon as a corpoRef appears.
  const [pendingPlan, setPendingPlan] = useState('');

  const plans = data?.payment?.corpo?.plans || [];

  const corpoRef = userData?.corpo?.ref;
  const isSubscribed = ['active', 'trialing'].includes(userData?.corpo?.subscription?.status);

  useEffect(() => {
    let mounted = true;
    loadStripe(process.env.NEXT_PUBLIC_STRIPE_API).then((s) => { if (mounted) setStripe(s); });
    return () => { mounted = false; };
  }, []);

  // Replay the intent after the login / corpo-creation detour.
  useEffect(() => {
    if (!pendingPlan || !corpoRef) return;
    setPendingPlan('');
    if (isSubscribed) router.push(legendHref);
    else setModalPlan(pendingPlan);
  }, [pendingPlan, corpoRef, isSubscribed, legendHref, router]);

  const onSelect = useCallback((planType) => {
    if (!userData?.ref) {
      setPendingPlan(planType);
      userAuthOpenModal('login');
      return;
    }
    // Already subscribed (or no corpo yet): nothing to sell here, the Legend+ page owns those cases.
    if (!corpoRef || isSubscribed) {
      router.push(legendHref);
      return;
    }
    setModalPlan(planType);
  }, [userData?.ref, corpoRef, isSubscribed, router, legendHref, userAuthOpenModal]);

  if (!plans.length) return null;

  return (
    <div className="w-full">
      <div className="grid gap-4 lg:grid-cols-2 lg:gap-6">
        {plans.map((p) => (
          <OfferCard
            key={p.planType}
            plan={p}
            plans={plans}
            t={t}
            lang={lang}
            selected={hovered === p.planType}
            dimmed={!!hovered && hovered !== p.planType}
            onSelect={onSelect}
            onHover={setHovered}
          />
        ))}
      </div>

      {modalPlan && (
        <div className="flex fixed inset-0 z-[100] justify-center items-center p-4 bg-black/70">
          {/* Light surface (charte: purple-0 #F2F1F9): SubscribeBlock renders dark text and the
              Stripe element on white — on a dark panel nothing would be readable. */}
          <div className="overflow-y-auto p-6 w-full max-w-2xl max-h-[90vh] rounded-2xl bg-purple-0">
            <div className="flex gap-3 justify-between items-start mb-4">
              <h3 className="text-lg font-medium text-purple-6">
                {t('corpo:abonnement.offerPaymentTitle')}
              </h3>
              <button
                type="button"
                className="text-2xl leading-none text-purple-5/60 hover:text-purple-6"
                onClick={() => setModalPlan('')}
                aria-label="close"
              >
                ×
              </button>
            </div>
            {stripe ? (
              <Elements stripe={stripe}>
                <SubscribeBlock
                  corpoRef={corpoRef}
                  corpoName={userData?.corpo?.name}
                  plans={plans}
                  initialPlanType={modalPlan}
                  onSubscribed={() => { setModalPlan(''); router.push(legendHref); }}
                />
              </Elements>
            ) : <Loading />}
          </div>
        </div>
      )}
    </div>
  );
}

// Preloaded query (useQueryLoader + usePreloadedQuery, Suspense handled inside) — the repo's
// standard loader, and it starts fetching before render instead of on render.
const QLContainer = miniQLWS({
  query: queryPublicPlans,
  providerVariables: () => ({}),
  Component: LegendPlusOffersContent,
});

export default function LegendPlusOffers() {
  return <QLContainer />;
}
